import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'

export interface SidebarTab {
  id: string
  label: string
  badge?: number
}

interface SidebarContextType {
  /** Aktuell angezeigte Sub-Tabs (leer = keine) */
  tabs: SidebarTab[]
  activeTab: string
  onTabChange: (id: string) => void
  /** Titel über den Sub-Tabs (z.B. Firmenname) */
  sectionTitle: string
  /** Seite registriert ihre Tabs */
  register: (
    tabs: SidebarTab[],
    activeTab: string,
    onTabChange: (id: string) => void,
    sectionTitle?: string,
  ) => void
  /** Seite entfernt ihre Tabs (bei unmount) */
  unregister: () => void
}

const SidebarContext = createContext<SidebarContextType | null>(null)

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<SidebarTab[]>([])
  const [activeTab, setActiveTab] = useState('')
  const [onTabChange, setOnTabChange] = useState<(id: string) => void>(() => () => {})
  const [sectionTitle, setSectionTitle] = useState('')

  const register = useCallback(
    (
      newTabs: SidebarTab[],
      newActiveTab: string,
      newOnTabChange: (id: string) => void,
      title = '',
    ) => {
      setTabs(newTabs)
      setActiveTab(newActiveTab)
      // useState mit Funktion: wrappen damit React es nicht direkt aufruft
      setOnTabChange(() => newOnTabChange)
      setSectionTitle(title)
    },
    [],
  )

  const unregister = useCallback(() => {
    setTabs([])
    setActiveTab('')
    setOnTabChange(() => () => {})
    setSectionTitle('')
  }, [])

  return (
    <SidebarContext.Provider
      value={{ tabs, activeTab, onTabChange, sectionTitle, register, unregister }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebarTabs() {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebarTabs must be inside <SidebarProvider>')
  return ctx
}
